/**
 * iter-219 — Features Context.
 *
 * Fetches /api/me/features once per session and exposes a `hasFeature(key)`
 * helper so the UI can hide tabs the user hasn't paid for.
 *
 * NOTE: this is UX only. The backend `feature_gate.FeatureGateMiddleware`
 * is the authoritative gate — this context merely prevents the user from
 * clicking into something they'll be bounced back out of.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FeaturesContext = createContext({ features: new Set(), hasFeature: () => true, loading: true });

export const FeaturesProvider = ({ children }) => {
  const { user } = useAuth();
  const [features, setFeatures] = useState(new Set());
  const [allFeatures, setAllFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setFeatures(new Set());
      setLoading(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/me/features`, { withCredentials: true });
      setFeatures(new Set(data.features || []));
      setAllFeatures(data.all_features || []);
    } catch (e) {
      // On failure, don't gate anything (fail-open UX; backend still gates server-side).
      setFeatures(new Set());
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const hasFeature = useCallback(
    (key) => {
      if (!ready) return true;       // optimistic until first fetch lands
      if (user?.role === 'admin') return true;
      return features.has(key);
    },
    [features, ready, user],
  );

  return (
    <FeaturesContext.Provider value={{ features, allFeatures, hasFeature, loading, reload }}>
      {children}
    </FeaturesContext.Provider>
  );
};

export const useFeatures = () => useContext(FeaturesContext);
